// Chat with the sponsorship assistant. It knows your profile, campaigns and
// deals, and remembers the conversation until you start a new one.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChat, useClearChat, useRunAgent, type Campaign } from '../../lib/sponsors';
import { useJob } from '../../lib/transcripts';
import { jobError } from '../../lib/jobs';
import { errorInfo } from '../../lib/api';
import { Button } from '../ui/Button';
import { ErrorMessage, Loading } from '../ui/Feedback';
import { useToast } from '../ui/Toast';

const SUGGESTIONS = [
  'Which deals should I follow up on this week?',
  'What should I charge per episode?',
  'Which kinds of brands fit my audience best?',
];

export function Assistant({ campaign }: { campaign: Campaign }) {
  const chat = useChat();
  const clear = useClearChat();
  const agent = useRunAgent();
  const toast = useToast();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useJob(jobId);
  const status = job.data?.status;
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === 'completed') {
      void qc.invalidateQueries({ queryKey: ['sponsor-chat'] }).then(() => setPending(null));
      setJobId(null);
    } else if (status === 'failed') {
      // Nothing was saved: put the question back so it can be retried.
      setPending((p) => {
        if (p) setText(p);
        return null;
      });
    }
  }, [status, qc]);

  const thinking = !!pending && status !== 'failed';
  const count = chat.data?.length ?? 0;
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [count, pending]);

  const ask = (message: string) => {
    const m = message.trim();
    if (!m || thinking) return;
    setPending(m);
    setText('');
    agent.mutate(
      { type: 'agent-chat', message: m, campaignId: campaign.id },
      {
        onSuccess: (r) => setJobId(r.jobId),
        onError: (err) => {
          setPending(null);
          setText(m);
          toast.show(errorInfo(err).message, 'error');
        },
      },
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    ask(text);
  };

  if (chat.isLoading) return <Loading />;
  if (chat.isError) return <ErrorMessage error={chat.error} onRetry={() => chat.refetch()} />;
  const messages = chat.data ?? [];

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted">Ask about your pipeline, pricing or who to pitch. It knows your show and your deals.</p>
        {messages.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            loading={clear.isPending}
            onClick={() => clear.mutate(undefined, { onSuccess: () => setJobId(null) })}
            disabled={thinking}
          >
            New conversation
          </Button>
        )}
      </div>

      <div role="log" aria-label="Conversation" className="max-h-[32rem] space-y-3 overflow-y-auto rounded-xl border border-border p-4">
        {messages.length === 0 && !pending && (
          <div className="space-y-2">
            <p className="text-sm text-muted">Try:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => ask(s)}
                  className="rounded-full border border-border px-3 py-1 text-sm hover:border-accent hover:text-accent"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} role={m.role} content={m.content} />
        ))}
        {pending && <Bubble role="user" content={pending} />}
        {thinking && (
          <p role="status" className="text-sm text-muted">
            Thinking…
          </p>
        )}
        {status === 'failed' && (
          <p className="text-sm text-danger" title={job.data?.errorMessage ?? undefined}>
            {jobError(job.data?.errorMessage ?? null)}
          </p>
        )}
        <div ref={end} />
      </div>

      <form onSubmit={submit} className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ask the sponsorship assistant…"
          aria-label="Message"
          maxLength={4000}
          className="min-w-0 flex-1 rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
        <Button type="submit" disabled={!text.trim() || thinking} loading={agent.isPending}>
          Send
        </Button>
      </form>
    </div>
  );
}

function Bubble({ role, content }: { role: string; content: string }) {
  const mine = role === 'user';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${mine ? 'bg-accent text-accent-contrast' : 'bg-surface text-text'}`}
      >
        <span className="sr-only">{mine ? 'You: ' : 'Assistant: '}</span>
        {content}
      </div>
    </div>
  );
}
